import React, { useEffect, useState, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faHome, faPlus, faTrash, faSearch, faLightbulb, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";

import { API_URL_ENDPOINT } from "../appsetting";
import { useApi } from "../helpers/useApi";
import {
  confirmDeleteToast,
  sendError,
  sendSuccess,
} from "../helpers/Helper";
import { Model } from "../components/Model";
import SignatureUploader from "../components/SignatureUploader";
import { Textbox } from "../components/Textbox";
import { theme } from "../constants/theme";
import AddPageImage from "../assets/img/addth.png";
import { ConfirmDeleteModal } from "../components/ConfirmDeleteModal";

export const Thoughts = () => {
  const callApi = useApi();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [showAddModel, setShowAddModel] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [thoughtToDelete, setThoughtToDelete] = useState(null);

  const methods = useForm({
    defaultValues: {
      id: "0",
      title: "",
      description: "",
      bookAttributes: [],
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
  } = methods;

  const [data, setData] = useState({
    items: [],
    pageNo: 1,
    pageSize: 12,
    totalPage: 0,
    totalRecords: 0,
  });

  useEffect(() => {
    getData(1);
    setCoverPreview({ documentURL: AddPageImage });
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        getData(1);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  const getData = async (cp = null, append = false) => {
    try {
      setDataLoading(true);
      const filters = [];
      if (searchTerm) filters.push(`SearchText:${searchTerm}`);

      const queryParams = [
        filters.length ? `filters=${filters.join(";")}` : null,
        `pageNo=${cp ?? data.pageNo}`,
        `pageSize=${data.pageSize}`,
        `sortOrder=desc`,
      ]
        .filter(Boolean)
        .join("&");

      const response = await callApi(API_URL_ENDPOINT.Thought + `?${queryParams}`);

      if (response.status) {
        if (append) {
          setData((prev) => ({
            ...response.data,
            items: [...prev.items, ...response.data.items],
          }));
        } else {
          setData(response.data);
        }
      }
    } catch (error) {
      sendError("Failed to fetch thoughts");
      setData((prev) => ({ ...prev, items: [] }));
    } finally {
      setDataLoading(false);
    }
  };

  const observer = useRef();
  const lastThoughtElementRef = useCallback((node) => {
    if (dataLoading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && data.pageNo < data.totalPage) {
        getData(data.pageNo + 1, true);
      }
    });

    if (node) observer.current.observe(node);
  }, [dataLoading, data.pageNo, data.totalPage]);

  const handleDelete = (item) => {
    setThoughtToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!thoughtToDelete) return;
    try {
      const response = await callApi(API_URL_ENDPOINT.Thought + `/delete-thought/${thoughtToDelete.id}`, {
        method: "DELETE",
      });

      if (response.status) {
        sendSuccess(response.message);
        getData(1);
        setShowDeleteModal(false);
        setThoughtToDelete(null);
      } else {
        throw new Error(response.message);
      }
    } catch (ex) {
      sendError(ex.message);
    }
  };

  const addThought = async (formValues) => {
    try {
      if (coverPreview?.fileKey == "") return;
      const rawDate = formValues?.sdate;
      let formattedDate = null;

      if (rawDate) {
        const date = new Date(rawDate);
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        formattedDate = `${day}-${month}`;
      }
      const payload = {
        "dayMonth": formattedDate,
        "imagePath": coverPreview?.fileKey ?? ""
      };

      const response = await callApi(API_URL_ENDPOINT.Thought, {
        method: "POST",
        data: payload,
      });

      if (response.status) {
        sendSuccess(response.message);
        setCoverPreview({ documentURL: AddPageImage });
        setShowAddModel(false);
        getData(1);
        reset();
      } else {
        throw new Error(response.message);
      }
    } catch (ex) {
      sendError(ex.message);
    }
  };

  const saveCoverToDB = async (data) => setCoverPreview(data);

  return (
    <div className="w-full">
      {/* 🔹 Add Thought Modal */}
      <Model show={showAddModel} size="md">
        <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 border-b border-accent/20 text-white rounded-t-2xl">
          <div className="flex items-center">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center mr-3 shadow-lg shadow-primary/30">
              <FontAwesomeIcon icon={faLightbulb} className="text-white text-sm" />
            </div>
            <h5 className="font-bold text-base m-0">Add Thought</h5>
          </div>
          <button
            onClick={() => setShowAddModel(false)}
            className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all text-white"
          >
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        <div className="p-6 bg-light">
          <div className="text-center mb-6">
            <div className="mx-auto w-[200px] h-[200px] rounded-xl overflow-hidden shadow-md border border-lightGray relative bg-white">
              <SignatureUploader
                imageURL={coverPreview?.documentURL}
                onUploaded={saveCoverToDB}
                height={200}
                width={200}
                showEdit
              />
            </div>
            <p className="text-xs text-gray mt-2 font-medium">Upload thought image</p>
          </div>

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(addThought)}>
              <div className="mb-6">
                <h6 className="mb-2 text-sm font-bold text-dark">📅 Date</h6>
                <Textbox
                  Label="Select Date"
                  name="sdate"
                  Type="date"
                  register={register}
                  errors={errors}
                  watch={watch}
                />
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-lightGray">
                <button
                  type="button"
                  onClick={() => setShowAddModel(false)}
                  className="px-4 py-2 rounded-lg bg-white border border-lightGray text-gray font-semibold text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-lg bg-gradient-to-br from-primary to-orange-600 text-white font-semibold text-sm shadow-md hover:shadow-lg disabled:opacity-70 transition-all"
                >
                  {isSubmitting ? 'Saving...' : 'Save Thought'}
                </button>
              </div>
            </form>
          </FormProvider>
        </div>
      </Model>

      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-lightGray">
        <div>
          <div className="flex items-center mt-0">
            <FontAwesomeIcon icon={faLightbulb} className="text-primary text-xl mr-3" />
            <h4 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-primary to-accent mb-0">
              Daily Thoughts
            </h4>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative w-full md:w-auto">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 rounded-lg border border-lightGray bg-white focus:ring-2 focus:ring-accent/20 focus:border-accent outline-none w-full md:w-56 transition-all"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setIsTyping(true);
              }}
            />
          </div>
          <button
            onClick={() => setShowAddModel(true)}
            className="bg-gradient-to-r from-primary to-orange-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg shadow-primary/30 hover:-translate-y-0.5 transition-all flex items-center gap-2 whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Thought
          </button>
        </div>
      </div>

      {/* Content Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-lightGray p-6 mb-8">
        {/* Stats Row */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-lightGray">
          <span className="text-sm text-gray">
            Showing <strong className="text-primary">{data.items.length}</strong> of{' '}
            <strong className="text-secondary">{data.totalRecords}</strong> thoughts
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {dataLoading && data.items.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
              <p className="text-gray">Loading thoughts...</p>
            </div>
          ) : data?.items?.length > 0 ? (
            data.items.map((item, index) => (
              <div
                key={index}
                ref={data.items.length === index + 1 ? lastThoughtElementRef : null}
                className="bg-white rounded-xl overflow-hidden border border-lightGray shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div className="aspect-square w-full border-b border-lightGray relative group">
                  <img
                    src={item?.imagePath}
                    alt="Thought"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-3">
                  <div className="flex justify-between items-center mb-3">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-50 text-dark border border-gray-200">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-warning" />
                      {item?.dayMonth || "-"}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDelete(item)}
                    className="w-full py-1.5 rounded-lg bg-danger/10 text-danger border border-danger/20 text-xs font-bold hover:bg-danger hover:text-white transition-all flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faTrash} /> Delete
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-16 bg-light rounded-xl border border-dashed border-gray-300">
              <FontAwesomeIcon
                icon={faLightbulb}
                className="text-5xl text-gray-300 mb-4"
              />
              <p className="text-lg text-gray font-medium">No thoughts found</p>
            </div>
          )}
        </div>

        {/* Load More Loader */}
        {dataLoading && data.items.length > 0 && (
          <div className="flex justify-center mt-8 mb-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
          </div>
        )}
      </div>
      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        itemName="Thought"
        message={<span>Are you sure you want to delete this thought? <br /> Date: <strong>{thoughtToDelete?.dayMonth}</strong></span>}
      />
    </div>
  );
};
